import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, AlertCircle, CheckCircle, Loader2, Eye, EyeOff, ArrowLeft, Home as HomeIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // States for request reset
  const [email, setEmail] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState('');

  // States for reset password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setRequestError('Please enter your email address');
      return;
    }

    setRequestLoading(true);
    setRequestError('');

    try {
      const resp = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to send reset email');
      }

      setRequestSent(true);
    } catch (err) {
      if (err.name === 'SyntaxError') {
        setRequestError('Failed to send reset email');
      } else {
        setRequestError(err.message || 'Failed to send reset email');
      }
    } finally {
      setRequestLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    if (!newPassword || !confirmPassword) {
      setResetError('Please fill in all fields');
      return;
    }
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setResetLoading(true);

    try {
      const resp = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      // Read response as text first to handle edge cases
      const text = await resp.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(resp.ok ? 'Unexpected response' : `Error: ${text || 'Unknown error'}`);
      }
      
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setResetSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      // Handle network errors or JSON parsing errors
      if (err.name === 'SyntaxError') {
        setResetError('Failed to reset password');
      } else {
        setResetError(err.message || 'Failed to reset password');
      }
    } finally {
      setResetLoading(false);
    }
  };

  // If we have a token, show reset password form
  if (token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-airbnb-light-gray to-white px-4" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 mb-6">
                <HomeIcon className="w-8 h-8 text-airbnb-pink" />
                <span className="text-2xl font-bold text-airbnb-gray">iHostPMS</span>
              </Link>
              <h1 className="text-2xl font-bold text-airbnb-gray">Reset Your Password</h1>
              <p className="text-airbnb-medium-gray mt-1">Enter your new password below</p>
            </div>

            {resetSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Password Reset Successful!</h2>
                <p className="text-gray-600 mb-4">Your password has been updated. Redirecting to login...</p>
                <Link to="/login" className="text-airbnb-pink font-semibold hover:underline">
                  Go to Login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div>
                  <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 h-12 border-gray-200 focus:border-airbnb-pink focus:ring-airbnb-pink/20"
                      placeholder="Create a new password"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                </div>

                <div>
                  <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12 border-gray-200 focus:border-airbnb-pink focus:ring-airbnb-pink/20"
                      placeholder="Confirm your new password"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                {resetError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{resetError}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full h-12 bg-airbnb-pink hover:bg-airbnb-dark-pink text-white font-semibold text-base rounded-xl transition-colors"
                  data-testid="reset-submit-btn"
                >
                  {resetLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show request reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-airbnb-light-gray to-white px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <HomeIcon className="w-8 h-8 text-airbnb-pink" />
              <span className="text-2xl font-bold text-airbnb-gray">iHostPMS</span>
            </Link>
            <h1 className="text-2xl font-bold text-airbnb-gray">Forgot Password?</h1>
            <p className="text-airbnb-medium-gray mt-1">Enter your email and we'll send you a reset link</p>
          </div>

          {requestSent ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Check Your Email</h2>
              <p className="text-gray-600 mb-4">
                If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Don't see it? Check your spam folder or try again.
              </p>
              <Button
                variant="outline"
                onClick={() => { setRequestSent(false); setEmail(''); }}
                className="w-full"
              >
                Try Another Email
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRequestReset} className="space-y-5">
              <div>
                <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 border-gray-200 focus:border-airbnb-pink focus:ring-airbnb-pink/20"
                    placeholder="you@example.com"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {requestError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{requestError}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={requestLoading}
                className="w-full h-12 bg-airbnb-pink hover:bg-airbnb-dark-pink text-white font-semibold text-base rounded-xl transition-colors"
                data-testid="request-submit-btn"
              >
                {requestLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-airbnb-pink font-semibold hover:underline">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
